import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createResourceCollectionSelector } from "jsonapi-zod-query";
import { z } from "zod";

export const caseFormSchemaInputAutocomplete = z.object({
    idUUID: z.string(),
    clientName: z.string().optional().nullable(),
    caseName: z.string().optional().nullable(),
    caseType: z.string().optional().nullable(),
});

const casesAutocompleteTypeSelector = createResourceCollectionSelector({
    type: "case",
    attributesSchema: caseFormSchemaInputAutocomplete,
});

export type AutocompleteCase = ReturnType<typeof casesAutocompleteTypeSelector>;

type Props = {
    searchString: string;
    limitToCurrentEmployee?: boolean;
};

export const useFetchCasesForAutocomplete = ({
    searchString,
    limitToCurrentEmployee,
}: Props): UseQueryResult<AutocompleteCase> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        enabled: searchString.length > 2,
        queryKey: [`cases-autocomplete-${searchString}`],
        queryFn: async () => {
            const fetchURL = apiUrl("/case/search/autocomplete");

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    searchString,
                    limitToCurrentEmployee,
                }),
            });

            return await response.json();
        },
        select: casesAutocompleteTypeSelector,
    });
};
